import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { getCurrentUser, getMyOrganization, updateMyOrganization, updateMyProfile, uploadAvatar, deleteAvatar, getAvatarUrl } from '../services/api';
import { Icons } from '../components/Icons';
import { ProfileSkeleton } from '../components/SkeletonLoader';

const collegeOptions = {
  College: ['CABHA', 'CAG', 'CAM', 'CAS', 'CEN', 'CIT', 'CTE', 'Graduate School', 'SRSCO'],
  Campus: ['Alabat', 'Catanauan', 'Gumaca', 'Infanta', 'Lucena', 'Polillo', 'Tayabas', 'Tiaong', 'Tagkawayan'],
};

const orgCategories = [
  'Academic',
  'Civic',
  'Cultural',
  'Religious',
  'Socio-Political',
  'Sports',
  'Student Council',
  'Others',
];
/* ─── Eye icon helpers ───────────────────────────────────────── */
const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7a9.96 9.96 0 015.46 1.625M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 3-4 7-9 7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
  </svg>
);
const EyeOn = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);
const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

/*
  PasswordField is defined OUTSIDE OrgProfile so React sees it as a
  stable component reference. If defined inside, React unmounts +
  remounts it every render, causing the input to lose focus after
  each keystroke (the one-word-at-a-time bug).
*/
const PasswordField = ({ label, name, value, onChange, showPw, onTogglePw, error }) => (
  <div>
    <label className="block text-[13px] font-bold text-gray-800 mb-2">{label}</label>
    <div className="relative">
      <input
        type={showPw ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full bg-[#f8fafc] border ${error ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 pr-12 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
      />
      <button
        type="button"
        onClick={onTogglePw}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
      >
        {showPw ? <EyeOff /> : <EyeOn />}
      </button>
    </div>
    {error && <p className="text-red-500 text-[11px] mt-1">{error}</p>}
  </div>
);

/* ─── Main profile page ──────────────────────────────────────── */
const OrgProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    userId: null,
    fullName: '',
    email: '',
    applicationType: 'accreditation',
    activityName: '',
    orgName: '',
    acronym: '',
    college: '',
    position: 'President', // No backend field exists for this yet — see signup.jsx note
    hasAvatar: false,
  });
  const [form, setForm] = useState({
    fullName: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    activityName: '',
    orgName: '',
    acronym: '',
    orgCategory: '',
    college: '',
    position: 'President',
    presidentContact: '',
    adviserName: '',
    adviserContact: '',
    totalMembers: '',
  });
  const [showPw, setShowPw] = useState({
    password: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [deletingAvatar, setDeletingAvatar] = useState(false);
  // Cache-busting value so the browser re-fetches the image after a new upload,
  // since the URL path itself doesn't change between uploads
  const [avatarVersion, setAvatarVersion] = useState(Date.now());

  const isOffCampus = profile.applicationType === 'offcampus';

  const getInitialForm = (p) => ({
    fullName: p.fullName || '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    activityName: p.activityName && p.activityName !== 'N/A' ? p.activityName : (p.orgName && p.orgName !== 'N/A' ? p.orgName : ''),
    orgName: p.orgName && p.orgName !== 'N/A' ? p.orgName : '',
    acronym: p.acronym && p.acronym !== 'N/A' ? p.acronym : '',
    orgCategory: p.orgCategory && p.orgCategory !== 'N/A' ? p.orgCategory : '',
    college: p.college && p.college !== 'N/A' ? p.college : '',
    position: p.position || 'President',
    presidentContact: p.presidentContact && p.presidentContact !== 'N/A' ? p.presidentContact : '',
    adviserName: p.adviserName && p.adviserName !== 'N/A' ? p.adviserName : '',
    adviserContact: p.adviserContact && p.adviserContact !== 'N/A' ? p.adviserContact : '',
    totalMembers: p.totalMembers !== null && p.totalMembers !== undefined && p.totalMembers !== 'N/A' ? String(p.totalMembers) : '',
  });

  // Fetches the real logged-in user and their organization on load
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const [userRes, orgRes] = await Promise.allSettled([
        getCurrentUser(),
        getMyOrganization(),
      ]);

      const user = userRes.status === 'fulfilled' ? userRes.value.data : null;
      const org = orgRes.status === 'fulfilled' ? orgRes.value.data : null;
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const applicationType = user?.application_type || storedUser.application_type || 'accreditation';

      const newProfile = {
        userId: user ? user.user_id : null,
        fullName: user ? user.full_name : '',
        email: user ? user.slsu_email : '',
        applicationType,
        activityName: org ? org.name : 'N/A',
        orgName: org ? org.name : 'N/A',
        acronym: org ? org.acronym || 'N/A' : 'N/A',
        college: org ? org.college || 'N/A' : 'N/A',
        orgCategory: org ? org.organization_category || '' : '',
        presidentContact: org ? org.president_contact || 'N/A' : 'N/A',
        adviserName: org ? org.adviser_name || 'N/A' : 'N/A',
        adviserContact: org ? org.adviser_contact || 'N/A' : 'N/A',
        totalMembers: org && org.total_members !== null && org.total_members !== undefined ? org.total_members : 'N/A',
        position: 'President',
        hasAvatar: !!(user && user.profile_picture_path),
      };

      setProfile(newProfile);
      setForm(getInitialForm(newProfile));
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const togglePw = (field) =>
    setShowPw((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Full name is required.';
    if (isOffCampus) {
      if (!form.activityName.trim()) errs.activityName = 'Activity name is required.';
    } else {
      if (!form.orgName.trim()) errs.orgName = 'Organization name is required.';
    }
    if (form.newPassword && form.newPassword !== form.confirmPassword)
      errs.confirmPassword = 'Passwords do not match.';
    if (form.newPassword && !form.password)
      errs.password = 'Enter your current password to set a new one.';
    return errs;
  };

  const handleStartEdit = () => {
    setForm(getInitialForm(profile));
    setErrors({});
    setSubmitError('');
    setIsEditing(true);
  };

  // Saves changes to the backend — profile, password, and organization/activity info
  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setErrors({});
    setSubmitError('');
    setSaving(true);

    try {
      // 1. Update personal info (name and/or password)
      const userPayload = { full_name: form.fullName.trim() };
      if (form.newPassword) {
        userPayload.current_password = form.password;
        userPayload.new_password = form.newPassword;
      }

      const userRes = await updateMyProfile(userPayload);

      // Keeps localStorage in sync so the sidebar/topbar reflect the new name immediately
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...storedUser, full_name: userRes.data.full_name }));

      // 2. Update organization or activity details
      const orgPayload = isOffCampus
        ? {
            name: form.activityName.trim(),
            college: form.college || null,
          }
        : {
            name: form.orgName.trim(),
            acronym: form.acronym.trim() || null,
            organization_category: form.orgCategory || null,
            college: form.college || null,
            president_contact: form.presidentContact.trim() || null,
            adviser_name: form.adviserName.trim() || null,
            adviser_contact: form.adviserContact.trim() || null,
            total_members: form.totalMembers !== '' && !isNaN(Number(form.totalMembers)) ? parseInt(form.totalMembers, 10) : null,
          };

      const orgRes = await updateMyOrganization(orgPayload);
      const updatedOrg = orgRes.data;

      setProfile((prev) => ({
        ...prev,
        fullName: userRes.data.full_name,
        activityName: updatedOrg.name,
        orgName: updatedOrg.name,
        acronym: updatedOrg.acronym || 'N/A',
        college: updatedOrg.college || 'N/A',
        orgCategory: updatedOrg.organization_category || '',
        presidentContact: updatedOrg.president_contact || 'N/A',
        adviserName: updatedOrg.adviser_name || 'N/A',
        adviserContact: updatedOrg.adviser_contact || 'N/A',
        totalMembers: updatedOrg.total_members !== null && updatedOrg.total_members !== undefined ? updatedOrg.total_members : 'N/A',
      }));

      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 3000);
      setForm((prev) => ({ ...prev, password: '', newPassword: '', confirmPassword: '' }));
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setSubmitError(error.response.data.detail);
      } else {
        setSubmitError('Failed to save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAvatar = async () => {
    setAvatarError('');
    setDeletingAvatar(true);
    try {
      await deleteAvatar();
      setProfile((prev) => ({ ...prev, hasAvatar: false }));
      setAvatarVersion(Date.now());
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        profile_picture_path: null
      }));
      window.dispatchEvent(new Event('avatarUpdated'));
    } catch (error) {
      setAvatarError('Failed to remove photo.');
    } finally {
      setDeletingAvatar(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        API.post("/auth/logout", { refresh_token: refreshToken }).catch(() => {});
      }
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      navigate("/login");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    setSubmitError('');
    setForm(getInitialForm(profile));
  };

  // Validates and uploads the selected avatar image immediately
  const handleAvatarSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAvatarError('');

    const validExt = /\.(png|jpe?g)$/i.test(file.name);
    if (!validExt) {
      setAvatarError('Only PNG and JPG images are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be smaller than 5MB.');
      e.target.value = '';
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAvatar(formData);

      setProfile((prev) => ({ ...prev, hasAvatar: !!res.data.profile_picture_path }));

      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        profile_picture_path: res.data.profile_picture_path,
        user_id: res.data.user_id
      }));
      window.dispatchEvent(new Event('avatarUpdated'));

      // Forces the browser to re-fetch the image instead of using a stale cached copy
      setAvatarVersion(Date.now());
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setAvatarError(error.response.data.detail);
      } else {
        setAvatarError('Failed to upload photo. Please try again.');
      }
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  // Uses the real uploaded photo if one exists, otherwise falls back to the
  // auto-generated initials avatar
  const avatarSrc = profile.hasAvatar && profile.userId
    ? `${getAvatarUrl(profile.userId, profile.profile_picture_path)}?v=${avatarVersion}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.fullName || 'User')}&background=0D9488&color=fff&size=128`;

  if (loading) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">Profile</h2>
        <p className="text-gray-500 text-sm mt-1">Manage your personal information and account settings.</p>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-green-700 text-sm font-medium">
          Profile updated successfully.
        </div>
      )}

      {/* Main card */}
      <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-8 flex flex-col md:flex-row gap-8 items-start">

          {/* ── Avatar card ── */}
          <div className="w-full md:w-[220px] flex-shrink-0 border border-gray-100 rounded-[16px] p-6 flex flex-col items-center shadow-sm bg-gray-50/50">
            <label className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md mb-4 cursor-pointer group">
              <img src={avatarSrc} alt="User avatar" className="w-full h-full object-cover" />
              {/* Hover overlay with camera icon */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                {uploadingAvatar ? (
                  <span className="text-[10px] font-bold">Uploading...</span>
                ) : (
                  <CameraIcon />
                )}
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={handleAvatarSelect}
                disabled={uploadingAvatar}
              />
            </label>
            {avatarError && (
              <p className="text-red-500 text-[11px] text-center mb-2">{avatarError}</p>
            )}
            {isEditing && profile.hasAvatar && (
              <button
                type="button"
                onClick={handleDeleteAvatar}
                disabled={deletingAvatar || uploadingAvatar}
                className="mt-2 text-[11px] font-bold text-red-500 hover:text-red-700 transition-colors disabled:opacity-60"
              >
                {deletingAvatar ? 'Removing...' : 'Remove photo'}
              </button>
            )}
            <p className="text-[15px] font-bold text-gray-900 text-center leading-snug">{profile.fullName}</p>
            <p className="text-[11px] text-gray-400 font-medium mt-0.5 text-center flex items-center space-x-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{profile.email}</span>
            </p>
            <p className="text-[10px] text-gray-300 font-medium mt-3 text-center">Click photo to change</p>
          </div>

          {/* ── Info / Edit pane ── */}
          <div className="flex-1 min-w-0">
            {!isEditing ? (
              /* ── VIEW mode ── */
              <div>
                <div className="flex justify-between items-center mb-5 gap-4">
                  <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider">Personal Information</p>
                  <button
                    onClick={handleStartEdit}
                    className="flex items-center justify-center space-x-1.5 bg-[#054334] hover:bg-[#086C53] text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors shadow-sm flex-shrink-0"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Profile</span>
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {[
                    { label: 'Full Name', value: profile.fullName },
                    { label: 'Email', value: profile.email },
                    { label: 'Password', value: 'Password has been set' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-[13px] font-bold text-gray-800">{label}</p>
                      <p className={`text-[13px] mt-0.5 ${label === 'Password' ? 'text-gray-400 italic' : 'text-gray-500'}`}>{value}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider mb-4">
                    {isOffCampus ? 'Activity Information' : 'Organization Information'}
                  </p>
                  <div className="space-y-4">
                    {isOffCampus ? (
                      [
                        { label: 'Activity Name', value: profile.activityName || profile.orgName },
                        { label: 'College/Campus', value: profile.college },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-[13px] font-bold text-gray-800">{label}</p>
                          <p className="text-[13px] text-gray-500 mt-0.5">{value}</p>
                        </div>
                      ))
                    ) : (
                      [
                        { label: 'Organization Name', value: profile.orgName },
                        { label: 'Acronym', value: profile.acronym },
                        { label: 'Type of Student Organization', value: profile.orgCategory || 'N/A' },
                        { label: 'College/Campus', value: profile.college },
                        { label: 'Position', value: profile.position },
                        { label: 'Contact Number of President', value: profile.presidentContact },
                        { label: 'Name of Adviser', value: profile.adviserName },
                        { label: 'Contact Number of Adviser', value: profile.adviserContact },
                        { label: 'Total Number of Members', value: profile.totalMembers },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-[13px] font-bold text-gray-800">{label}</p>
                          <p className="text-[13px] text-gray-500 mt-0.5">{value}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5 mt-8 md:hidden">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 hover:bg-red-100 font-bold py-3.5 rounded-xl transition-colors shadow-sm"
                  >
                    <Icons.LogOut />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            ) : (
              /* ── EDIT mode ── */
              <div>
                <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider mb-5">Personal Information</p>

                {submitError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-[12px] font-medium">
                    {submitError}
                  </div>
                )}

                <div className="space-y-4 max-w-xl">
                  {/* Full Name */}
                  <div>
                    <label className="block text-[13px] font-bold text-gray-800 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      className={`w-full bg-[#f8fafc] border ${errors.fullName ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
                    />
                    {errors.fullName && <p className="text-red-500 text-[11px] mt-1">{errors.fullName}</p>}
                  </div>

                  <PasswordField
                    label="Current Password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    showPw={showPw.password}
                    onTogglePw={() => togglePw('password')}
                    error={errors.password}
                  />
                  <PasswordField
                    label="New Password"
                    name="newPassword"
                    value={form.newPassword}
                    onChange={handleChange}
                    showPw={showPw.newPassword}
                    onTogglePw={() => togglePw('newPassword')}
                    error={errors.newPassword}
                  />
                  <div className="mt-1.5 space-y-1">
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      form.newPassword.length >= 8 ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{form.newPassword.length >= 8 ? '✓' : '○'}</span>
                      At least 8 characters
                    </p>
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      /[A-Z]/.test(form.newPassword) ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{/[A-Z]/.test(form.newPassword) ? '✓' : '○'}</span>
                      At least one uppercase letter
                    </p>
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      /[0-9]/.test(form.newPassword) ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{/[0-9]/.test(form.newPassword) ? '✓' : '○'}</span>
                      At least one number
                    </p>
                  </div>
                  <PasswordField
                    label="Confirm New Password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    showPw={showPw.confirmPassword}
                    onTogglePw={() => togglePw('confirmPassword')}
                    error={errors.confirmPassword}
                  />
                  <p className="text-[11px] text-gray-400">Leave the password fields blank to keep your current password.</p>

                  {/* ── Organization / Activity Information fields ── */}
                  <div className="border-t border-gray-100 pt-6 mt-6">
                    <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider mb-5">
                      {isOffCampus ? 'Activity Information' : 'Organization Information'}
                    </p>

                    {isOffCampus ? (
                      /* Off-Campus Activity Edit Fields */
                      <div className="space-y-4">
                        {/* Activity Name */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Activity Name</label>
                          <input
                            type="text"
                            name="activityName"
                            value={form.activityName}
                            onChange={handleChange}
                            className={`w-full bg-[#f8fafc] border ${errors.activityName ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
                          />
                          {errors.activityName && <p className="text-red-500 text-[11px] mt-1">{errors.activityName}</p>}
                        </div>

                        {/* College/Campus */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">College/Campus</label>
                          <select
                            name="college"
                            value={form.college}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                          >
                            <option value="">— Select College/Campus —</option>
                            {form.college &&
                              !collegeOptions.College.includes(form.college) &&
                              !collegeOptions.Campus.includes(form.college) && (
                                <option value={form.college}>{form.college}</option>
                            )}
                            <optgroup label="Colleges">
                              {collegeOptions.College.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Campuses">
                              {collegeOptions.Campus.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      </div>
                    ) : (
                      /* Accreditation Organization Edit Fields */
                      <div className="space-y-4">
                        {/* Organization Name */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Organization Name</label>
                          <input
                            type="text"
                            name="orgName"
                            value={form.orgName}
                            onChange={handleChange}
                            className={`w-full bg-[#f8fafc] border ${errors.orgName ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
                          />
                          {errors.orgName && <p className="text-red-500 text-[11px] mt-1">{errors.orgName}</p>}
                        </div>

                        {/* Acronym */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Acronym</label>
                          <input
                            type="text"
                            name="acronym"
                            placeholder="e.g. CABHA-SC"
                            value={form.acronym}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        {/* Type of Student Organization */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Type of Student Organization</label>
                          <select
                            name="orgCategory"
                            value={form.orgCategory}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                          >
                            <option value="">— Select Type of Organization —</option>
                            {orgCategories.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        {/* College/Campus */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">College/Campus</label>
                          <select
                            name="college"
                            value={form.college}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                          >
                            <option value="">— Select College/Campus —</option>
                            {form.college &&
                              !collegeOptions.College.includes(form.college) &&
                              !collegeOptions.Campus.includes(form.college) && (
                                <option value={form.college}>{form.college}</option>
                            )}
                            <optgroup label="Colleges">
                              {collegeOptions.College.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Campuses">
                              {collegeOptions.Campus.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </optgroup>
                          </select>
                        </div>

                        {/* Position */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Position</label>
                          <input
                            type="text"
                            name="position"
                            value={form.position || 'President'}
                            readOnly
                            disabled
                            className="w-full bg-gray-100 border border-gray-200 rounded-xl px-5 py-3.5 text-[14px] text-gray-500 cursor-not-allowed"
                          />
                        </div>

                        {/* Contact Number of President */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Contact Number of President</label>
                          <input
                            type="text"
                            name="presidentContact"
                            placeholder="e.g. 09123456789"
                            value={form.presidentContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        {/* Name of Adviser */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Name of Adviser</label>
                          <input
                            type="text"
                            name="adviserName"
                            placeholder="e.g. Prof. Juan Dela Cruz"
                            value={form.adviserName}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        {/* Contact Number of Adviser */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Contact Number of Adviser</label>
                          <input
                            type="text"
                            name="adviserContact"
                            placeholder="e.g. 09123456789"
                            value={form.adviserContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        {/* Total Number of Members */}
                        <div>
                          <label className="block text-[13px] font-bold text-gray-800 mb-2">Total Number of Members</label>
                          <input
                            type="number"
                            name="totalMembers"
                            min="0"
                            placeholder="e.g. 50"
                            value={form.totalMembers}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2.5 bg-[#054334] hover:bg-[#086C53] text-white text-sm font-bold rounded-lg transition-colors shadow-sm disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="px-6 py-2.5 border border-gray-200 text-gray-600 text-sm font-bold rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgProfile;